import type { Row } from 'common/types/scalarSpreadsheet';
import {
  SpreadsheetConfig,
  SpreadsheetConfigConstructorParams,
} from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import type {
  ConfigurationCell,
  ConfigurationCellMap,
  ICell,
  ParserParams,
  ReverseParserParams,
} from 'models/interfaces/spreadsheet-types/core-types';
import { useCrossReferenceStore } from 'store/useCrossReferenceStore';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import {
  CreateCrossReferencePopupSheetParams,
  CrossRefSheetColumn,
  CrossRefSheetInitialObject,
  CrossRefSheetTableData,
  entryAlias,
  TITLE_ROW_ALIAS,
} from './createCrossReferencePopupSheet.types';
import type { CrossReferenceConfig, CrossReferenceRowState } from './types';
import type { InheritedCellFormat } from './utils/extractFormatFromCell';

// `ValueEditor.getTargetValue` strips commas/percent upstream, but for plain
// (non-expression) numeric input the value lands at `reverseParser` as the
// raw user-typed string — `Cell.fitValue` only runs inside the `isExpr`
// branch of `calcValue`. Coerce to JS `number` here, and treat empty/blank
// as `null` so the "drop fully-empty row" guard below still works.
const toNumericRowValue = (rawValue: unknown): number | null => {
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (trimmed === '') {
      return null;
    }
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

const METADATA_COL_WIDTH = '160px';
const DEFAULT_FIRST_COL_WIDTH = '175px';

function getWidth(parentWidth: number): string {
  return parentWidth > 0 ? `${parentWidth}px` : DEFAULT_FIRST_COL_WIDTH;
}

type PopupRow = Row<object, CrossRefSheetInitialObject, CrossRefSheetColumn, CrossRefSheetTableData>;

// Build the rowConfig array for a popup whose data rows are the given entries.
// Always appends one trailing empty row in multi_row mode so the user has a
// next row to type into; in single_cell mode the trailing row is added only
// when there are no entries yet.
const buildPopupRowConfig = (entries: CrossReferenceRowState[], config: CrossReferenceConfig): PopupRow[] => {
  const showTitleRow = config.rowMode !== 'single_cell';
  const rowConfig: PopupRow[] = [];
  if (showTitleRow) {
    rowConfig.push({ alias: TITLE_ROW_ALIAS, readOnly: true, gridType: 'string' });
  }
  entries.forEach(entry => {
    rowConfig.push({ alias: entryAlias(entry.position), readOnly: false });
  });
  if (config.rowMode === 'multi_row') {
    const trailingPosition = entries.length > 0 ? Math.max(...entries.map(e => e.position)) + 1 : 0;
    rowConfig.push({ alias: entryAlias(trailingPosition), readOnly: false });
  } else if (entries.length === 0) {
    rowConfig.push({ alias: entryAlias(0), readOnly: false });
  }
  return rowConfig;
};

// Map { entryAlias(position) -> value } for a given column index. Column 0 is
// the value column; the rest are metadata columns keyed by `col.alias`.
const buildColumnValues = (
  entries: CrossReferenceRowState[],
  col: { alias: string },
  colIndex: number
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  entries.forEach(r => {
    const key = entryAlias(r.position);
    values[key] = colIndex === 0 ? r.value : (r.metadata?.[col.alias] ?? '');
  });
  return values;
};

// Mutate an existing popup sheet so it reflects a new entries list. Updates
// `columns[i].values` and `rowConfig` in place; the caller is responsible for
// re-evaluating the sheet (so cells/data are rebuilt) and triggering React
// re-render. Keeping the SpreadsheetConfig identity stable lets ScalarDatasheet
// preserve its internal state (selected cell, editing input, focus) while
// rows are added or removed.
export const syncPopupSheetToEntries = (
  sheet: SpreadsheetConfig<CrossRefSheetInitialObject, CrossRefSheetColumn, CrossRefSheetTableData>,
  entries: CrossReferenceRowState[],
  config: CrossReferenceConfig
): void => {
  // The sheet is the live SpreadsheetConfig; mutating it in place is the
  // documented auto-grow contract (see header comment on this module).
  /* eslint-disable no-param-reassign */
  sheet.rowConfig = buildPopupRowConfig(entries, config) as ICell[];
  sheet.columns.forEach((col, idx) => {
    col.values = buildColumnValues(entries, col, idx);
  });
  /* eslint-enable no-param-reassign */
};

// Build the SpreadsheetConfig for one cross-ref popup sheet.
//
// The popup owns its own SpreadsheetConfig instance and a local useWorkbook
// (see CrossReferencePopupSheet). When the user fills the trailing row,
// reverseParser writes to the cross-ref store; the popup observes the entry
// count change, calls `syncPopupSheetToEntries` to mutate this same instance
// in place, and re-evaluates locally — the host workbook is never touched, so
// host-cell edits in progress aren't disrupted.
//
// Save flow: reverseParser mirrors edits into the cross-ref store; the host's
// save reads dirty entries from the store (see distributeDirtyPayload), so the
// popup doesn't need to be in the host workbook for save to work.
export const createCrossReferencePopupSheet = (
  params: CreateCrossReferencePopupSheetParams
): SpreadsheetConfig<CrossRefSheetInitialObject, CrossRefSheetColumn, CrossRefSheetTableData> => {
  const { cellKey, initialRows, config, inheritedFormat, parentWidth } = params;
  const alphabet = alphabetGenerator([], config.columns.length);

  const entries = initialRows.filter(r => !r.isPlaceholder);
  const showTitleRow = config.rowMode !== 'single_cell';

  const columns: CrossRefSheetColumn[] = config.columns.map((col, idx) => ({
    alias: col.alias,
    header: col.header ?? col.alias,
    gridType: idx === 0 ? 'number' : 'string',
    format: idx === 0 ? (inheritedFormat.format ?? undefined) : undefined,
    width: idx === 0 ? getWidth(parentWidth) : METADATA_COL_WIDTH,
    values: buildColumnValues(entries, col, idx),
  }));

  const rowConfig = buildPopupRowConfig(entries, config);

  type PopupConfigurationCell = ConfigurationCell<
    object,
    CrossRefSheetInitialObject,
    CrossRefSheetColumn,
    CrossRefSheetTableData
  >;
  type PopupConfigurationCellMap = ConfigurationCellMap<
    object,
    CrossRefSheetInitialObject,
    CrossRefSheetColumn,
    CrossRefSheetTableData
  >;

  const parser = (
    p: ParserParams<CrossRefSheetColumn, CrossRefSheetInitialObject, CrossRefSheetTableData, object>
  ): PopupConfigurationCellMap => {
    const cells: PopupConfigurationCellMap = {};
    const { columns: cols, rowConfig: rows } = p;
    rows.forEach((row, rowIndex) => {
      cols.forEach((column, colIndex) => {
        const colLegend = alphabet[colIndex];
        const rowNumber = rowIndex + 1;
        const key = `${colLegend}${rowNumber}`;
        const isTitle = row.alias === TITLE_ROW_ALIAS;

        if (isTitle) {
          cells[key] = {
            key,
            alias: row.alias,
            value: column.header,
            readOnly: true,
            gridType: 'string',
            columnLegend: colLegend,
            rowNumber,
            className: 'table-header',
          } as PopupConfigurationCell;
          return;
        }

        const rawValue = column.values[row.alias ?? ''];
        cells[key] = {
          key,
          alias: row.alias,
          value: (rawValue as ICell['value']) ?? '',
          gridType: column.gridType,
          format: column.format,
          currency: column.gridType === 'number' ? inheritedFormat.currency : undefined,
          readOnly: false,
          columnLegend: colLegend,
          rowNumber,
        } as PopupConfigurationCell;
      });
    });
    return cells;
  };

  // Reverse-parses popup cells back into the cross-reference store. The
  // returned shape mirrors what `column.values` looks like, so the standard
  // `reversedData[]` collection in `useWorkbook.onChange` keeps a typed
  // record (we don't read it, but returning a real INITIAL_OBJECT keeps the
  // contract honest — see ScalarSpreadsheet.md §19).
  const reverseParser = (
    rp: ReverseParserParams<CrossRefSheetInitialObject, CrossRefSheetColumn, CrossRefSheetTableData, object>
  ): CrossRefSheetInitialObject => {
    const { cells } = rp;
    const cols = rp.columns as CrossRefSheetColumn[];
    const rows = rp.rowConfig;
    const nextRows: CrossReferenceRowState[] = [];

    rows.forEach((row, rowIndex) => {
      if (row.alias === TITLE_ROW_ALIAS) {
        return;
      }
      // Position is the index among data rows. When a title row is present it
      // sits at rowIndex 0, so data rows start at rowIndex 1; otherwise data
      // rows start at rowIndex 0.
      const position = showTitleRow ? rowIndex - 1 : rowIndex;
      const valueCellKey = `${alphabet[0]}${rowIndex + 1}`;
      const numericValue = toNumericRowValue(cells[valueCellKey]?.value);

      const metadata: Record<string, unknown> = {};
      cols.forEach((col, colIndex) => {
        if (colIndex === 0) {
          return;
        }
        const metaCellKey = `${alphabet[colIndex]}${rowIndex + 1}`;
        const metaValue = cells[metaCellKey]?.value;
        metadata[col.alias] = metaValue ?? '';
      });

      // Drop fully-empty rows. The popup component watches the resulting
      // entry count and re-syncs the sheet — promoted entries get a fresh
      // trailing row appended; cleared entries drop out of rowConfig.
      const metadataAllEmpty = Object.values(metadata).every(v => v == null || v === '');
      if (numericValue === null && metadataAllEmpty) {
        return;
      }

      nextRows.push({
        position,
        value: numericValue,
        metadata,
        isPlaceholder: false,
      });
    });

    // Mirror edits into the cross-reference store. The popup runs inside its
    // own workbook (see CrossReferencePopupSheet) and the host save flow
    // reads dirty entries out of the store via `distributeDirtyPayload`, so
    // this is the canonical sync surface for popup edits.
    useCrossReferenceStore.getState().setRows(cellKey, nextRows);

    return { cellKey, inheritedFormat, config };
  };

  const tableSlug = `xref-${cellKey.replace(/[^a-z0-9_-]/g, '-')}`;

  // SpreadsheetConfigConstructorParams extends ScalarSpreadsheetConfigDataInterface,
  // which technically requires runtime methods (`evaluate`, `applyConditions`,
  // `reverseParse`, …) that the constructor itself supplies on the resulting
  // instance. Other call sites in the codebase work around this by spread-
  // casting; we narrow the cast to a `Partial<...> as full` shape so the
  // value object is still type-checked field-by-field.
  type PopupParams = SpreadsheetConfigConstructorParams<
    CrossRefSheetInitialObject,
    CrossRefSheetColumn,
    CrossRefSheetTableData,
    object
  >;

  const constructorParams: Partial<PopupParams> = {
    name: `xref_${cellKey}`,
    columns,
    rowConfig: rowConfig as ICell[],
    parser,
    reverseParser,
    tableData: { cellKey },
    initialObj: { cellKey, inheritedFormat, config },
    tableTerms: { tableSlug },
    showTitlesColumn: false,
    showTotalColumn: false,
    showToolbar: false,
    hasColTitle: showTitleRow,
    colTitleRow: showTitleRow ? 0 : undefined,
    allowEmptyValues: true,
    displayRowIndicator: false,
    displayColumnIndicator: false,
    shouldValidate: false,
    ignoreCellExpressionErrors: false,
  };

  return new SpreadsheetConfig<CrossRefSheetInitialObject, CrossRefSheetColumn, CrossRefSheetTableData>(
    constructorParams as unknown as PopupParams
  );
};

// Re-export so the popup component can import alongside the factory.
export type { CrossReferenceConfig, InheritedCellFormat };

export default createCrossReferencePopupSheet;
